import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  Users, Clock, Edit2, CheckCircle, XCircle, Search, Filter,
  ChevronDown, AlertTriangle, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const REASON_CODES = [
  { value: 'system_error', label: 'System Error' },
  { value: 'forgot_punch', label: 'Forgot to Punch' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'manager_request', label: 'Manager Request' },
  { value: 'schedule_change', label: 'Schedule Change' },
  { value: 'other', label: 'Other' }
];

export default function ManagerPage() {
  const { api, user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Adjustment Modal State
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    new_timestamp: '',
    reason_code: ''
  });
  
  // Excuse Modal State
  const [showExcuseModal, setShowExcuseModal] = useState(false);
  const [excuseData, setExcuseData] = useState({
    status: 'excused',
    reason: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, entriesRes] = await Promise.all([
        api.get('/manager/team-members'),
        api.get('/manager/team-entries')
      ]);
      setTeamMembers(membersRes.data);
      setTimeEntries(entriesRes.data);
    } catch (error) {
      toast.error('Failed to fetch team data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdjustTime = async () => {
    if (!adjustmentData.new_timestamp || !adjustmentData.reason_code) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await api.post('/manager/adjust-time', {
        entry_id: selectedEntry.id,
        new_timestamp: adjustmentData.new_timestamp,
        reason_code: adjustmentData.reason_code
      });
      toast.success('Time entry adjusted successfully');
      setShowAdjustModal(false);
      setSelectedEntry(null);
      setAdjustmentData({ new_timestamp: '', reason_code: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Adjustment failed');
    }
  };

  const handleExcuseStatus = async () => {
    if (!excuseData.reason) {
      toast.error('Please provide a reason');
      return;
    }

    try {
      await api.post('/manager/excuse-status', {
        entry_id: selectedEntry.id,
        status: excuseData.status,
        reason: excuseData.reason
      });
      toast.success(`Entry marked as ${excuseData.status}`);
      setShowExcuseModal(false);
      setSelectedEntry(null);
      setExcuseData({ status: 'excused', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const getMemberName = (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    return member?.full_name || 'Unknown';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPunchTypeLabel = (type) => {
    const labels = {
      shift_start: 'Shift Start',
      shift_end: 'Shift End',
      lunch_start: 'Lunch Start',
      lunch_end: 'Lunch End',
      break_start: 'Break Start',
      break_end: 'Break End'
    };
    return labels[type] || type;
  };

  const getPunchTypeBadge = (type) => {
    const colors = {
      shift_start: 'bg-[#10B981]/20 text-[#10B981]',
      shift_end: 'bg-[#EF4444]/20 text-[#EF4444]',
      lunch_start: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      lunch_end: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      break_start: 'bg-[#4B167A]/40 text-white',
      break_end: 'bg-[#4B167A]/40 text-white'
    };
    return colors[type] || 'bg-white/10 text-white';
  };

  const filteredEntries = timeEntries.filter(entry => {
    if (selectedMember !== 'all' && entry.user_id !== selectedMember) return false;
    if (searchQuery) {
      const memberName = getMemberName(entry.user_id).toLowerCase();
      if (!memberName.includes(searchQuery.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="manager-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Team <span className="text-[#D4AF37]">Management</span>
            </h1>
            <p className="text-white/50 mt-1">Review and adjust team time entries</p>
          </div>
        </div>

        {/* Team Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Team Size</p>
                  <p className="text-2xl font-bold text-white">{teamMembers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Active Now</p>
                  <p className="text-2xl font-bold text-white">
                    {teamMembers.filter(m => m.is_active).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Late Today</p>
                  <p className="text-2xl font-bold text-white">
                    {timeEntries.filter(e => e.late_reason).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#4B167A]/40 flex items-center justify-center">
                  <Edit2 className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Adjusted</p>
                  <p className="text-2xl font-bold text-white">
                    {timeEntries.filter(e => e.is_adjusted).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="glass-card border-0">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                    data-testid="input-search"
                  />
                </div>
              </div>
              
              <Select value={selectedMember} onValueChange={setSelectedMember}>
                <SelectTrigger className="w-full md:w-48 bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-member">
                  <SelectValue placeholder="Select Member" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                  <SelectItem value="all">All Members</SelectItem>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-full md:w-40 bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-date">
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Time Entries Table */}
        <Card className="glass-card border-0">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-white">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
              Time Entries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Timestamp</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-white/50">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredEntries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-white/50">
                        No time entries found
                      </td>
                    </tr>
                  ) : (
                    filteredEntries.map((entry) => (
                      <tr key={entry.id} data-testid={`entry-row-${entry.id}`}>
                        <td>
                          <div className="font-medium text-white">{getMemberName(entry.user_id)}</div>
                        </td>
                        <td>
                          <Badge className={getPunchTypeBadge(entry.punch_type)}>
                            {getPunchTypeLabel(entry.punch_type)}
                          </Badge>
                        </td>
                        <td className="font-mono text-sm text-white/70">
                          {formatTimestamp(entry.timestamp)}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {entry.is_adjusted && (
                              <Badge className="bg-[#4B167A]/40 text-white">Adjusted</Badge>
                            )}
                            {entry.training_mode && (
                              <Badge className="bg-[#D4AF37]/20 text-[#D4AF37]">Training</Badge>
                            )}
                            {entry.late_reason && (
                              <Badge className="bg-[#F59E0B]/20 text-[#F59E0B]">Late</Badge>
                            )}
                          </div>
                        </td>
                        <td className="text-white/50 text-sm max-w-xs truncate">
                          {entry.late_reason || entry.adjustment_reason || '-'}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[#D4AF37] hover:text-[#D4AF37]/80 hover:bg-white/5"
                              onClick={() => {
                                setSelectedEntry(entry);
                                setAdjustmentData({
                                  new_timestamp: entry.timestamp,
                                  reason_code: ''
                                });
                                setShowAdjustModal(true);
                              }}
                              data-testid={`btn-adjust-${entry.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {entry.punch_type === 'shift_end' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-[#F59E0B] hover:text-[#F59E0B]/80 hover:bg-white/5"
                                onClick={() => {
                                  setSelectedEntry(entry);
                                  setShowExcuseModal(true);
                                }}
                                data-testid={`btn-excuse-${entry.id}`}
                              >
                                <AlertTriangle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Time Adjustment Modal */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#D4AF37]" />
              Adjust Time Entry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-white/70 mb-2">
                Adjusting entry for: <span className="text-white font-medium">{getMemberName(selectedEntry?.user_id)}</span>
              </p>
              <p className="text-white/50 text-sm">
                Original: {selectedEntry && formatTimestamp(selectedEntry.timestamp)}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">New Timestamp</Label>
              <Input
                type="datetime-local"
                value={adjustmentData.new_timestamp.slice(0, 16)}
                onChange={(e) => setAdjustmentData({
                  ...adjustmentData,
                  new_timestamp: new Date(e.target.value).toISOString()
                })}
                className="bg-[#0A091E]/50 border-white/20 text-white"
                data-testid="input-new-timestamp"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Reason Code</Label>
              <Select
                value={adjustmentData.reason_code}
                onValueChange={(value) => setAdjustmentData({ ...adjustmentData, reason_code: value })}
              >
                <SelectTrigger className="bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-reason-code">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                  {REASON_CODES.map((code) => (
                    <SelectItem key={code.value} value={code.value}>
                      {code.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowAdjustModal(false)}
              className="text-white/70"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdjustTime}
              className="bg-[#D4AF37] text-[#0A091E]"
              data-testid="btn-confirm-adjust"
            >
              Save Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excuse Status Modal */}
      <Dialog open={showExcuseModal} onOpenChange={setShowExcuseModal}>
        <DialogContent className="bg-[#1F1B71] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
              Mark Left Early Status
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Status</Label>
              <Select
                value={excuseData.status}
                onValueChange={(value) => setExcuseData({ ...excuseData, status: value })}
              >
                <SelectTrigger className="bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-excuse-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                  <SelectItem value="excused">Excused</SelectItem>
                  <SelectItem value="unexcused">Unexcused</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Reason</Label>
              <Textarea
                value={excuseData.reason}
                onChange={(e) => setExcuseData({ ...excuseData, reason: e.target.value })}
                placeholder="Provide a reason..."
                className="bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30"
                data-testid="input-excuse-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowExcuseModal(false)}
              className="text-white/70"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExcuseStatus}
              className="bg-[#D4AF37] text-[#0A091E]"
              data-testid="btn-confirm-excuse"
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
